import React, { useState, useRef, useEffect } from 'react';
import Lottie from 'lottie-react';
import { FiX, FiSend, FiUser } from 'react-icons/fi';
import chatbotAnimation from '../lottie_animation/chatbot.json';
import { getGroqResponse, shouldUseAI } from '../services/groqService';
import '../styles/components/Chatbot.css';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState(null);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: 'Hello! I am MEDC BOT, your virtual assistant. Welcome to MEDCoop!\n\nPlease select your preferred language:',
      options: [
        'English',
        'Tagalog',
        'Cebuano'
      ],
      isLanguageSelect: true
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [showPrompt, setShowPrompt] = useState(true);
  const messagesEndRef = useRef(null);

  // Language-specific responses
  const translations = {
    greeting: {
      english: 'Great! I\'ll assist you in English. How can I help you today?',
      tagalog: 'Salamat! Tutulong ako sa iyo sa Tagalog. Paano kita matutulungan ngayon?',
      cebuano: 'Salamat! Motabang ko nimo sa Cebuano. Unsaon nako pagtabang nimo karon?'
    },
    options: {
      english: ['About MEDC', 'Our Services', 'How to Apply', 'Contact Us', 'Other Questions'],
      tagalog: ['Tungkol sa MEDC', 'Mga Serbisyo', 'Paano Mag-Apply', 'Makipag-ugnayan', 'Iba pang Tanong'],
      cebuano: ['Bahin sa MEDC', 'Mga Serbisyo', 'Unsaon Pag-Apply', 'Kontaka Kami', 'Uban pang Pangutana']
    },
    about: {
      english: 'Employees\' Development Cooperative (MEDC) was established on October 29, 1997. We are registered with the Cooperative Development Authority (CDA) under Registration Number 6520-11009760. Today, we have a workforce of more than 2,500 personnel distributed across Luzon, Visayas, and Mindanao.',
      tagalog: 'Ang Employees\' Development Cooperative (MEDC) ay itinatag noong Oktubre 29, 1997. Kami ay rehistrado sa Cooperative Development Authority (CDA) sa ilalim ng Registration Number 6520-11009760. Sa kasalukuyan, mayroon kaming higit sa 2,500 empleyado na nakakalat sa Luzon, Visayas, at Mindanao.',
      cebuano: 'Ang Employees\' Development Cooperative (MEDC) gitukod niadtong Oktubre 29, 1997. Kami rehistrado sa Cooperative Development Authority (CDA) ubos sa Registration Number 6520-11009760. Karon, aduna kami labaw sa 2,500 ka trabahante nga nagkatibulaag sa Luzon, Visayas, ug Mindanao.'
    },
    services: {
      english: 'MEDCoop offers comprehensive manpower services including:\n\n• Security Services\n• Janitorial Services\n• Messengerial Services\n• Utility Personnel\n• General Services\n\nAll our personnel undergo thorough screening and training.',
      tagalog: 'Ang MEDCoop ay nag-aalok ng komprehensibong serbisyo ng manpower kabilang ang:\n\n• Security Services\n• Janitorial Services\n• Messengerial Services\n• Utility Personnel\n• General Services\n\nAng lahat ng aming tauhan ay sumasailalim sa masusing pagsusuri at pagsasanay.',
      cebuano: 'Ang MEDCoop nagtanyag og komprehensibong serbisyo sa manpower lakip ang:\n\n• Security Services\n• Janitorial Services\n• Messengerial Services\n• Utility Personnel\n• General Services\n\nAng tanan namong tauhan nag-agian og bug-os nga pag-screen ug pagbansay.'
    },
    apply: {
      english: 'To apply for a job at MEDCoop, follow these steps:\n\n📋 REQUIREMENTS:\n• NBI Clearance\n• Police Clearance\n• Barangay Clearance\n• Valid Government ID\n• Updated Resume/CV\n• Medical Certificate\n• 2x2 and 1x1 ID Photos\n\n📝 HOW TO APPLY:\n1. Visit our office at 3rd Floor Marcela Building, Palma Gil Street, Davao City\n2. Fill out the application form\n3. Submit your complete requirements\n4. Wait for interview schedule\n5. Attend orientation if hired\n\n📍 You can also apply online through our website\'s Membership page!\n\n📞 For inquiries: (082) 285-1120 or (082) 224-6036',
      tagalog: 'Para mag-apply sa MEDCoop, sundin ang mga hakbang na ito:\n\n📋 MGA REQUIREMENTS:\n• NBI Clearance\n• Police Clearance\n• Barangay Clearance\n• Valid Government ID\n• Na-update na Resume/CV\n• Medical Certificate\n• 2x2 at 1x1 ID Photos\n\n📝 PAANO MAG-APPLY:\n1. Pumunta sa aming opisina sa 3rd Floor Marcela Building, Palma Gil Street, Davao City\n2. Sagutan ang application form\n3. I-submit ang kumpletong requirements\n4. Hintayin ang schedule ng interview\n5. Dumalo sa orientation kapag na-hire\n\n📍 Pwede ka ring mag-apply online sa Membership page ng aming website!\n\n📞 Para sa mga katanungan: (082) 285-1120 o (082) 224-6036',
      cebuano: 'Aron mag-apply sa MEDCoop, sunda kining mga lakang:\n\n📋 MGA REQUIREMENTS:\n• NBI Clearance\n• Police Clearance\n• Barangay Clearance\n• Valid Government ID\n• Updated nga Resume/CV\n• Medical Certificate\n• 2x2 ug 1x1 ID Photos\n\n📝 UNSAON PAG-APPLY:\n1. Adto sa among opisina sa 3rd Floor Marcela Building, Palma Gil Street, Davao City\n2. Pun-a ang application form\n3. I-submit ang kompleto nga requirements\n4. Hulata ang schedule sa interview\n5. Adto sa orientation kung ma-hire\n\n📍 Mahimo usab mag-apply online pinaagi sa Membership page sa among website!\n\n📞 Para sa mga pangutana: (082) 285-1120 o (082) 224-6036'
    },
    requirements: {
      english: 'Here are the requirements to apply at MEDCoop:\n\n📋 DOCUMENTS NEEDED:\n• NBI Clearance (original)\n• Police Clearance (original)\n• Barangay Clearance (original)\n• Valid Government ID (photocopy)\n• Updated Resume/CV\n• Medical Certificate\n• 2x2 ID Photo (2 pieces)\n• 1x1 ID Photo (2 pieces)\n\n💡 TIP: Make sure all clearances are updated and not expired!',
      tagalog: 'Narito ang mga requirements para mag-apply sa MEDCoop:\n\n📋 MGA DOKUMENTO:\n• NBI Clearance (original)\n• Police Clearance (original)\n• Barangay Clearance (original)\n• Valid Government ID (photocopy)\n• Na-update na Resume/CV\n• Medical Certificate\n• 2x2 ID Photo (2 piraso)\n• 1x1 ID Photo (2 piraso)\n\n💡 TIP: Siguraduhing updated at hindi expired ang lahat ng clearances!',
      cebuano: 'Ania ang mga requirements aron mag-apply sa MEDCoop:\n\n📋 MGA DOKUMENTO:\n• NBI Clearance (original)\n• Police Clearance (original)\n• Barangay Clearance (original)\n• Valid Government ID (photocopy)\n• Updated nga Resume/CV\n• Medical Certificate\n• 2x2 ID Photo (2 ka piraso)\n• 1x1 ID Photo (2 ka piraso)\n\n💡 TIP: Siguradohag updated ug dili expired ang tanan nga clearances!'
    },
    benefits: {
      english: 'As a MEDCoop employee, you will enjoy these benefits:\n\n✅ Government Mandated Benefits:\n• Updated SSS Remittances\n• PhilHealth Coverage\n• Pag-IBIG Fund Contribution\n• Employees Compensation\n\n✅ Company Benefits:\n• 13th Month Pay\n• 5 Days Incentive Leave with Pay\n• Uniform Allowance\n• Training and Development Programs\n• Career Growth Opportunities',
      tagalog: 'Bilang empleyado ng MEDCoop, matatanggap mo ang mga benepisyong ito:\n\n✅ Government Mandated Benefits:\n• Updated SSS Remittances\n• PhilHealth Coverage\n• Pag-IBIG Fund Contribution\n• Employees Compensation\n\n✅ Company Benefits:\n• 13th Month Pay\n• 5 Days Incentive Leave with Pay\n• Uniform Allowance\n• Training at Development Programs\n• Career Growth Opportunities',
      cebuano: 'Isip empleyado sa MEDCoop, matagamtaman nimo kining mga benepisyo:\n\n✅ Government Mandated Benefits:\n• Updated SSS Remittances\n• PhilHealth Coverage\n• Pag-IBIG Fund Contribution\n• Employees Compensation\n\n✅ Company Benefits:\n• 13th Month Pay\n• 5 Days Incentive Leave with Pay\n• Uniform Allowance\n• Training ug Development Programs\n• Career Growth Opportunities'
    },
    membership: {
      english: 'Interested in joining MEDCoop? We offer great benefits including:\n\n• Updated SSS Remittances\n• PhilHealth Coverage\n• Pag-IBIG Fund\n• 13th Month Pay\n• 5 Days Incentive Leave with Pay\n• Employees Compensation\n\nVisit our Membership page to learn more!',
      tagalog: 'Interesado bang sumali sa MEDCoop? Nag-aalok kami ng magagandang benepisyo tulad ng:\n\n• Updated SSS Remittances\n• PhilHealth Coverage\n• Pag-IBIG Fund\n• 13th Month Pay\n• 5 Days Incentive Leave with Pay\n• Employees Compensation\n\nBisitahin ang aming Membership page para sa karagdagang impormasyon!',
      cebuano: 'Interesado ka mosalmot sa MEDCoop? Nagtanyag kami og maayong mga benepisyo lakip ang:\n\n• Updated SSS Remittances\n• PhilHealth Coverage\n• Pag-IBIG Fund\n• 13th Month Pay\n• 5 Days Incentive Leave with Pay\n• Employees Compensation\n\nBisitaha ang among Membership page aron mahibal-an pa!'
    },
    contact: {
      english: 'You can reach MEDCoop through:\n\n📍 Address: 3rd Floor Marcela Building, Palma Gil Street, Barangay 34-D, Davao City 8000\n\n📞 Phone Numbers:\n• (082) 285-1120\n• (082) 224-6036\n\n📧 Email: medabordo@medc.ph\n\n🌐 Website: www.medcoop.ph\n\n⏰ Office Hours: Monday to Saturday, 8:00 AM - 5:00 PM\n\nOur team is ready to assist you!',
      tagalog: 'Maaari mong kontakin ang MEDCoop sa pamamagitan ng:\n\n📍 Address: 3rd Floor Marcela Building, Palma Gil Street, Barangay 34-D, Davao City 8000\n\n📞 Mga Numero ng Telepono:\n• (082) 285-1120\n• (082) 224-6036\n\n📧 Email: medabordo@medc.ph\n\n🌐 Website: www.medcoop.ph\n\n⏰ Oras ng Opisina: Lunes hanggang Sabado, 8:00 AM - 5:00 PM\n\nHanda ang aming team na tumulong sa iyo!',
      cebuano: 'Mahimo nimong kontakon ang MEDCoop pinaagi sa:\n\n📍 Address: 3rd Floor Marcela Building, Palma Gil Street, Barangay 34-D, Davao City 8000\n\n📞 Mga Numero sa Telepono:\n• (082) 285-1120\n• (082) 224-6036\n\n📧 Email: medabordo@medc.ph\n\n🌐 Website: www.medcoop.ph\n\n⏰ Oras sa Opisina: Lunes hangtod Sabado, 8:00 AM - 5:00 PM\n\nAndam ang among team sa pagtabang nimo!'
    },
    default: {
      english: 'Thank you for your message! For more specific information, please feel free to browse our website or contact us directly. Is there anything else I can help you with?',
      tagalog: 'Salamat sa iyong mensahe! Para sa mas espesipikong impormasyon, maaari kang mag-browse sa aming website o makipag-ugnayan sa amin nang direkta. May iba pa ba akong maitutulong sa iyo?',
      cebuano: 'Salamat sa imong mensahe! Para sa mas espesipikong impormasyon, palihug pag-browse sa among website o kontaka kami direkta. Aduna pa bay lain nga akong matabangan nimo?'
    }
  };

  const floatingPrompts = [
    "Hello! I'm MEDC BOT, chat with me!",
    "Let's chat!",
    "Do you have any questions?",
    "Ask me anything about MEDCOOP!"
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Rotate through floating prompts
  useEffect(() => {
    if (!isOpen) {
      const interval = setInterval(() => {
        setShowPrompt(false);
        setTimeout(() => {
          setCurrentPromptIndex((prev) => (prev + 1) % floatingPrompts.length);
          setShowPrompt(true);
        }, 300);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [isOpen, floatingPrompts.length]);

  const getLanguageKey = () => {
    if (!language) return 'english';
    return language.toLowerCase();
  };

  const getMenuOptions = () => {
    const lang = getLanguageKey();
    return translations.options[lang] || translations.options.english;
  };

  const getBotResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    const lang = getLanguageKey();

    // About MEDC responses
    if (lowerMessage.includes('about') || lowerMessage.includes('medc') || lowerMessage.includes('company') || lowerMessage.includes('story') || lowerMessage.includes('tungkol') || lowerMessage.includes('bahin')) {
      return {
        text: translations.about[lang],
        options: getMenuOptions().slice(1, 4)
      };
    }

    // Services responses
    if (lowerMessage.includes('service') || lowerMessage.includes('offer') || lowerMessage.includes('provide') || lowerMessage.includes('serbisyo')) {
      return {
        text: translations.services[lang],
        options: [getMenuOptions()[0], getMenuOptions()[2], getMenuOptions()[3]]
      };
    }

    // Application/How to Apply responses - SPECIFIC
    if (lowerMessage.includes('apply') || lowerMessage.includes('how to') || lowerMessage.includes('paano') || lowerMessage.includes('unsaon') || lowerMessage.includes('mag-apply') || lowerMessage.includes('pag-apply') || lowerMessage.includes('application') || lowerMessage.includes('hire') || lowerMessage.includes('work') || lowerMessage.includes('trabaho') || lowerMessage.includes('empleyo')) {
      return {
        text: translations.apply[lang],
        options: [getMenuOptions()[0], getMenuOptions()[1], getMenuOptions()[3]]
      };
    }

    // Requirements responses - SPECIFIC
    if (lowerMessage.includes('requirement') || lowerMessage.includes('document') || lowerMessage.includes('clearance') || lowerMessage.includes('need') || lowerMessage.includes('bring') || lowerMessage.includes('kailangan') || lowerMessage.includes('dalhin') || lowerMessage.includes('kinahanglan')) {
      return {
        text: translations.requirements[lang],
        options: [getMenuOptions()[2], getMenuOptions()[3]]
      };
    }

    // Benefits responses - SPECIFIC
    if (lowerMessage.includes('benefit') || lowerMessage.includes('salary') || lowerMessage.includes('pay') || lowerMessage.includes('sweldo') || lowerMessage.includes('sahod') || lowerMessage.includes('sss') || lowerMessage.includes('philhealth') || lowerMessage.includes('pag-ibig') || lowerMessage.includes('13th') || lowerMessage.includes('leave')) {
      return {
        text: translations.benefits[lang],
        options: [getMenuOptions()[2], getMenuOptions()[3]]
      };
    }

    // Membership responses (general)
    if (lowerMessage.includes('member') || lowerMessage.includes('join') || lowerMessage.includes('career') || lowerMessage.includes('job') || lowerMessage.includes('sumali') || lowerMessage.includes('mosalmot')) {
      return {
        text: translations.membership[lang],
        options: getMenuOptions().slice(0, 4)
      };
    }

    // Contact responses
    if (lowerMessage.includes('contact') || lowerMessage.includes('reach') || lowerMessage.includes('phone') || lowerMessage.includes('email') || lowerMessage.includes('address') || lowerMessage.includes('location') || lowerMessage.includes('makipag') || lowerMessage.includes('kontaka')) {
      return {
        text: translations.contact[lang],
        options: [getMenuOptions()[0], getMenuOptions()[1], getMenuOptions()[2]]
      };
    }

    // Default response
    return {
      text: translations.default[lang],
      options: getMenuOptions()
    };
  };

  const handleLanguageSelect = (selectedLanguage) => {
    setLanguage(selectedLanguage);
    setMessages(prev => [
      ...prev,
      { type: 'user', text: selectedLanguage }
    ]);
    setIsTyping(true);

    setTimeout(() => {
      const lang = selectedLanguage.toLowerCase();
      setMessages(prev => [
        ...prev,
        {
          type: 'bot',
          text: translations.greeting[lang],
          options: translations.options[lang]
        }
      ]);
      setIsTyping(false);
    }, 1000);
  };

  const handleSendMessage = async (message) => {
    if (!message.trim()) return;

    // Add user message
    setMessages(prev => [...prev, { type: 'user', text: message }]);
    setInputValue('');
    setIsTyping(true);

    const lang = getLanguageKey();

    // Check if we should use AI or predefined responses
    if (shouldUseAI(message)) {
      // Use Groq AI for complex/other questions
      const aiResponse = await getGroqResponse(message, lang);
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: aiResponse.text, 
        options: getMenuOptions().slice(0, 4)
      }]);
      setIsTyping(false);
    } else {
      // Use predefined responses for common questions
      setTimeout(() => {
        const response = getBotResponse(message);
        setMessages(prev => [...prev, { type: 'bot', text: response.text, options: response.options }]);
        setIsTyping(false);
      }, 800);
    }
  };

  const handleOptionClick = (option, isLanguageSelect) => {
    if (isLanguageSelect) {
      handleLanguageSelect(option);
    } else {
      handleSendMessage(option);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendMessage(inputValue);
  };

  return (
    <div className="chatbot-container">
      {/* Floating Prompt Bubble */}
      {!isOpen && (
        <div className={`chatbot-prompt-bubble ${showPrompt ? 'show' : ''}`}>
          <span>{floatingPrompts[currentPromptIndex]}</span>
          <button 
            className="prompt-close"
            onClick={(e) => {
              e.stopPropagation();
              setShowPrompt(false);
            }}
          >
            <FiX />
          </button>
        </div>
      )}

      {/* Chat Toggle Button */}
      <button 
        className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? <FiX /> : <Lottie animationData={chatbotAnimation} loop={true} className="chatbot-lottie" />}
        {!isOpen && <span className="chat-badge">Chat</span>}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <Lottie animationData={chatbotAnimation} loop={true} className="header-lottie" />
              </div>
              <div>
                <h4>MEDC BOT</h4>
                <span className="chatbot-status">Online</span>
              </div>
            </div>
            <button className="chatbot-close" onClick={() => setIsOpen(false)}>
              <FiX />
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((message, index) => (
              <div key={index} className={`chat-message ${message.type}`}>
                {message.type === 'bot' && (
                  <div className="message-avatar">
                    <Lottie animationData={chatbotAnimation} loop={true} className="message-lottie" />
                  </div>
                )}
                {message.type === 'user' && (
                  <div className="message-avatar user">
                    <FiUser />
                  </div>
                )}
                <div className="message-content">
                  <p>{message.text}</p>
                  {message.options && (
                    <div className="message-options">
                      {message.options.map((option, idx) => (
                        <button
                          key={idx}
                          className="option-button"
                          onClick={() => handleOptionClick(option, message.isLanguageSelect)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="chat-message bot">
                <div className="message-avatar">
                  <Lottie animationData={chatbotAnimation} loop={true} className="message-lottie" />
                </div>
                <div className="message-content typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form className="chatbot-input" onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Type your message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <button type="submit" disabled={!inputValue.trim()}>
              <FiSend />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Chatbot;

